import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { toast } from "sonner";
import { ExtraForm } from "./extras/ExtraForm";
import { ExtraListItem } from "./extras/ExtraListItem";

const emptyForm = { label: "", price: "" };

export const AdminPrices = () => {
  const [extras, setExtras] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(() => api.get("/extras").then((r) => setExtras(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (extra) => {
    setEditingId(extra.id);
    setForm({ label: extra.label, price: extra.price });
  };

  const submit = async () => {
    if (!form.label.trim() || form.price === "") {
      toast.error("Completa el nombre y el precio del extra");
      return;
    }
    if (Number(form.price) < 0) {
      toast.error("El precio no puede ser negativo");
      return;
    }
    setSaving(true);
    const payload = { label: form.label.trim(), price: Number(form.price) };
    try {
      if (editingId) {
        await api.put(`/admin/extras/${editingId}`, payload);
        toast.success("Extra actualizado");
      } else {
        await api.post("/admin/extras", payload);
        toast.success("Extra agregado");
      }
      resetForm();
      await load();
    } catch {
      toast.error("Error al guardar el extra");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/extras/${id}`);
      toast.success("Extra eliminado");
      await load();
    } catch {
      toast.error("Error al eliminar el extra");
    }
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = [...extras];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setDragIdx(null);
    setExtras(reordered);
    try {
      await api.put("/admin/extras/reorder", { ordered_ids: reordered.map((e) => e.id) });
    } catch {
      toast.error("Error al reordenar");
      load();
    }
  };

  return (
    <Card className="p-6 space-y-6" data-testid="admin-prices-panel">
      <div>
        <h3 className="font-semibold">Precios de extras (MXN)</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Crea, edita, elimina y reordena (arrastrando) los extras opcionales que verán tus clientes en el cotizador público.
        </p>
      </div>

      <div className="space-y-2">
        {extras.length === 0 ? (
          <p data-testid="admin-extras-empty" className="text-sm text-muted-foreground">
            Aún no has agregado ningún extra.
          </p>
        ) : (
          extras.map((extra, idx) => (
            <ExtraListItem
              key={extra.id}
              extra={extra}
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              onEdit={() => startEdit(extra)}
              onDelete={() => remove(extra.id)}
            />
          ))
        )}
      </div>

      <ExtraForm form={form} setForm={setForm} editingId={editingId} saving={saving} onSubmit={submit} onCancel={resetForm} />
    </Card>
  );
};

